import React, { useMemo } from 'react';
import { defineMessages, IntlShape, useIntl } from 'react-intl';
import { MUSIC24_TRIAL_LEVEL_TIER } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../common/types';
import { Panel, PanelBody, PanelHead } from '../../music24/components/primitives/Panel';
import { planNameMessages } from '../../music24/config/music24PlanNames';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
import { TrialDifference, trialDifferences } from './trialDifferencesModel';

const messages = defineMessages({
  title: { id: 'app.sml.settings.subscription.trialDiff.title', defaultMessage: 'How the trial differs from {plan}' },
  // QA 2026-10-28 skp-16: the credits count by calendar month, also in the trial. A trial that runs
  // into a new month gets the credits again on the 1st, as the Overview and the plan table say.
  videoCredits: {
    id: 'app.sml.settings.subscription.trialDiff.videoCreditsMonth',
    defaultMessage:
      '{credits} video credits a month, not {full}. The count starts again on the 1st of each month, also in the trial.',
  },
  previews: {
    id: 'app.sml.settings.subscription.trialDiff.previews',
    defaultMessage: 'Pictures and videos are previews: half size, with a watermark.',
  },
  renderLast: {
    id: 'app.sml.settings.subscription.trialDiff.renderLast',
    defaultMessage: 'Your files are made after the files of paid plans.',
  },
  badge: {
    id: 'app.sml.settings.subscription.trialDiff.badge',
    defaultMessage: 'Your SmartLink pages show the MUSIC24 badge.',
  },
  noCustomDomain: {
    id: 'app.sml.settings.subscription.trialDiff.noCustomDomain',
    defaultMessage: 'No custom domain.',
  },
  noX: { id: 'app.sml.settings.subscription.trialDiff.noX', defaultMessage: 'No posts to X.' },
  oneArtist: {
    id: 'app.sml.settings.subscription.trialDiff.oneArtist',
    defaultMessage: 'One artist. Extra artists come with a paid plan.',
  },
  noAiClips: {
    id: 'app.sml.settings.subscription.trialDiff.noAiClips',
    defaultMessage: 'No AI clips for your music video.',
  },
  pitches: {
    id: 'app.sml.settings.subscription.trialDiff.pitchesPerDay',
    defaultMessage: '{perDay, plural, one {# new pitch a day} other {# new pitches a day}}.',
  },
  noAutopilot: {
    id: 'app.sml.settings.subscription.trialDiff.noAutopilot',
    defaultMessage: 'No Outreach autopilot.',
  },
  autoPostsPerDay: {
    id: 'app.sml.settings.subscription.trialDiff.autoPostsPerDay',
    defaultMessage: 'An Auto campaign posts at most {perDay, plural, one {# time} other {# times}} a day.',
  },
});

function lineText(intl: IntlShape, line: TrialDifference): string {
  switch (line.kind) {
    case 'videoCredits':
      return intl.formatMessage(messages.videoCredits, { credits: line.credits, full: line.full });
    case 'pitches':
      return intl.formatMessage(messages.pitches, { perDay: line.perDay });
    case 'autoPostsPerDay':
      return intl.formatMessage(messages.autoPostsPerDay, { perDay: line.perDay });
    case 'previews':
    case 'renderLast':
    case 'badge':
    case 'noCustomDomain':
    case 'noX':
    case 'oneArtist':
    case 'noAiClips':
    case 'noAutopilot':
      return intl.formatMessage(messages[line.kind]);
  }
}

/**
 * Settings → Subscription during the trial: every way the trial differs from the plan whose limits
 * it gives (QA 2026-10-28, skp-8). The lines come from the plan table (`trialDifferences`).
 */
const SubscriptionTrialDifferencesComponent: React.FC = () => {
  const intl = useIntl();
  const lines = useMemo(() => trialDifferences(), []);
  if (lines.length === 0) {
    return null;
  }
  return (
    <Panel className="mb-4" data-testid="subscription-trial-differences">
      <PanelHead
        title={intl.formatMessage(messages.title, {
          plan: intl.formatMessage(planNameMessages[MUSIC24_TRIAL_LEVEL_TIER]),
        })}
      />
      <PanelBody>
        <ul className="mb-0 ps-3">
          {lines.map((line) => (
            <li key={line.kind}>{withBrandWord(lineText(intl, line))}</li>
          ))}
        </ul>
      </PanelBody>
    </Panel>
  );
};

export const SubscriptionTrialDifferences = TypedMemo(SubscriptionTrialDifferencesComponent);
